import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isTablerIconClass } from '../model/nodeTypes/registry';

export interface ContextMenuItem {
    label: string;
    icon?: string;
    onSelect: () => void;
    danger?: boolean;
}

interface ContextMenuProps {
    x: number;
    y: number;
    items: ContextMenuItem[];
    onClose: () => void;
}

/**
 * Generic right-click context menu, positioned at fixed screen coordinates
 * (the caller decides what triggers it — pane vs. node right-click, etc.).
 *
 * Closes on outside click or Escape. The outside-click listener runs on the
 * capture phase so it fires before React Flow's own pane/node click
 * handlers, otherwise a click that opens a *different* menu could get
 * immediately undone by this one's own close-on-outside-click logic running
 * after the new menu already mounted.
 */
export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x, y });

    // Clamp after mount, once the menu's actual rendered size is known, so
    // it never gets cut off when opened near the right/bottom edge.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const clampedX = Math.max(8, Math.min(x, window.innerWidth - rect.width - 8));
        const clampedY = Math.max(8, Math.min(y, window.innerHeight - rect.height - 8));
        setPosition({ x: clampedX, y: clampedY });
    }, [x, y]);

    useEffect(() => {
        const handlePointerDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as globalThis.Node)) {
                onClose();
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    return (
        <div
            ref={ref}
            className="card shadow-sm"
            style={{
                position: 'fixed',
                top: position.y,
                left: position.x,
                zIndex: 1000,
                minWidth: 190,
                maxHeight: 320,
                overflowY: 'auto',
                padding: '4px 0',
            }}
        >
            {items.map((item, idx) => (
                <button
                    key={idx}
                    type="button"
                    className={`dropdown-item d-flex align-items-center gap-2 ${item.danger ? 'text-danger' : ''}`}
                    style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}
                    onClick={() => {
                        item.onSelect();
                        onClose();
                    }}
                >
                    {item.icon &&
                        (isTablerIconClass(item.icon) ? (
                            <i className={`ti ${item.icon}`} />
                        ) : (
                            <span>{item.icon}</span>
                        ))}
                    <span>{item.label}</span>
                </button>
            ))}
        </div>
    );
}
